import React, { useState } from 'react';
import type {
  PollScaleSlide,
  PollSliderSlide,
  PollSlide,
  PollRuntime,
  PollWordCloudSlide,
} from '../../core/types/payloads/poll.types';
import { LiveChoiceBars } from '../shared/LiveChoiceBars';
import { resolveWordCloudGroups } from './poll.logic';
import { WordCloudDisplay } from './WordCloudDisplay';
import { useRoomParticipants } from '../../core/client/hooks';

const WORD_COLORS = ['#e879f9', '#a855f7', '#6366f1', '#ec4899', '#22d3ee', '#fbbf24'];

function getSlideAgg(runtime: PollRuntime, slideId: string) {
  return runtime.aggregates?.bySlide?.[slideId];
}

export { WordCloudDisplay };

export function ScaleDistribution({
  slide,
  runtime,
  size = 'md',
  highlightValue,
}: {
  slide: PollScaleSlide;
  runtime: PollRuntime;
  size?: 'md' | 'lg';
  highlightValue?: number;
}) {
  const agg = getSlideAgg(runtime, slide.id);
  const values = agg?.kind === 'scale' ? agg.values : [];
  const counts = Array.from({ length: slide.max - slide.min + 1 }, (_, i) => {
    const v = slide.min + i;
    return values.filter((x) => x === v).length;
  });
  const total = values.length;
  const average = agg?.kind === 'scale' ? agg.average : 0;

  return (
    <div>
      {total > 0 && (
        <p className={`mb-4 text-center font-display font-black text-[var(--sala-electric-purple)] ${size === 'lg' ? 'text-4xl' : 'text-2xl'}`}>
          Media {average.toFixed(1)}
          <span className="ml-2 text-sm font-bold text-slate-600">/ {slide.max}</span>
        </p>
      )}
      <LiveChoiceBars
        size={size}
        total={total}
        items={counts.map((count, i) => {
          const value = slide.min + i;
          return {
            label: String(value),
            count,
            accent: WORD_COLORS[i % WORD_COLORS.length],
            isSelected: highlightValue === value,
          };
        })}
      />
      {(slide.minLabel || slide.maxLabel) && (
        <div className="mt-2 flex justify-between text-xs font-bold text-slate-600">
          <span>{slide.minLabel ?? slide.min}</span>
          <span>{slide.maxLabel ?? slide.max}</span>
        </div>
      )}
    </div>
  );
}

export function SliderGauge({
  slide,
  runtime,
  size = 'md',
  highlightValue,
}: {
  slide: PollSliderSlide;
  runtime: PollRuntime;
  size?: 'md' | 'lg';
  highlightValue?: number;
}) {
  const agg = getSlideAgg(runtime, slide.id);
  const values = agg?.kind === 'slider' ? agg.values : [];
  const total = values.length;
  const average = agg?.kind === 'slider' ? agg.average : slide.min;
  const pct =
    slide.max > slide.min
      ? Math.round(((average - slide.min) / (slide.max - slide.min)) * 100)
      : 0;

  return (
    <div>
      <div className="mb-6 text-center">
        <p className="text-xs font-black uppercase tracking-widest text-slate-600">Promedio del grupo</p>
        <p className={`mt-1 font-display font-black text-[var(--sala-electric-purple)] ${size === 'lg' ? 'text-6xl' : 'text-4xl'}`}>
          {total > 0 ? average.toFixed(1) : '—'}
        </p>
        <p className="mt-1 text-sm font-bold text-slate-600">
          Rango {slide.min} – {slide.max}
        </p>
      </div>

      <div className="plugin-progress-bar relative h-4">
        <div className="plugin-progress-fill absolute inset-y-0 left-0 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
        {typeof highlightValue === 'number' && (
          <div
            className="absolute top-1/2 h-6 w-1 -translate-y-1/2 rounded-full border border-slate-950 bg-white shadow-[2px_2px_0_0_#020617]"
            style={{
              left: `${Math.round(((highlightValue - slide.min) / (slide.max - slide.min)) * 100)}%`,
            }}
          />
        )}
      </div>

      <p className="mt-4 text-[10px] font-black uppercase tracking-widest text-slate-600">
        {total} respuesta{total !== 1 ? 's' : ''}
      </p>
    </div>
  );
}

export function PollSlideAggregate({
  slide,
  runtime,
  size = 'md',
  highlightValue,
  slideIndex = 0,
  highlightParticipantId,
  maskOtherNames = false,
}: {
  slide: PollSlide;
  runtime: PollRuntime;
  size?: 'md' | 'lg';
  highlightValue?: unknown;
  slideIndex?: number;
  highlightParticipantId?: string;
  maskOtherNames?: boolean;
}) {
  const { participants } = useRoomParticipants();

  if (slide.kind === 'multiple-choice') return null;

  if (slide.kind === 'word-cloud') {
    const data = resolveWordCloudGroups(slide, runtime, participants, {
      slideIndex,
      highlightParticipantId,
      maskOtherNames,
    });
    return (
      <WordCloudDisplay
        data={data}
        size={size}
        highlightParticipantId={highlightParticipantId}
      />
    );
  }

  if (slide.kind === 'scale') {
    return (
      <ScaleDistribution
        slide={slide}
        runtime={runtime}
        size={size}
        highlightValue={typeof highlightValue === 'number' ? highlightValue : undefined}
      />
    );
  }

  if (slide.kind === 'slider') {
    return (
      <SliderGauge
        slide={slide}
        runtime={runtime}
        size={size}
        highlightValue={typeof highlightValue === 'number' ? highlightValue : undefined}
      />
    );
  }

  return null;
}

export function WordCloudInput({
  slide,
  onSubmit,
}: {
  slide: PollWordCloudSlide;
  onSubmit: (words: string[]) => void;
}) {
  const max = slide.maxWordsPerStudent ?? 3;
  const [text, setText] = useState('');

  const handleSubmit = () => {
    const words = text
      .split(/[,;\n]+/)
      .map((w) => w.trim())
      .filter(Boolean)
      .slice(0, max);
    if (words.length === 0) return;
    onSubmit(words);
  };

  return (
    <div className="mt-6 space-y-3">
      <input
        type="text"
        className="sala-input-field"
        placeholder={`Escribe hasta ${max} palabra${max !== 1 ? 's' : ''} (separadas por coma)`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSubmit();
        }}
      />
      <button type="button" className="sala-btn-hero w-full text-xs" onClick={handleSubmit}>
        Enviar palabras
      </button>
    </div>
  );
}

export function ScaleInput({
  slide,
  onSubmit,
}: {
  slide: PollScaleSlide;
  onSubmit: (value: number) => void;
}) {
  const points = Array.from({ length: slide.max - slide.min + 1 }, (_, i) => slide.min + i);

  return (
    <div className="mt-6">
      {(slide.minLabel || slide.maxLabel) && (
        <div className="mb-3 flex justify-between text-xs font-bold text-slate-600">
          <span>{slide.minLabel ?? slide.min}</span>
          <span>{slide.maxLabel ?? slide.max}</span>
        </div>
      )}
      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(points.length, 6)}, minmax(0, 1fr))` }}>
        {points.map((value, i) => (
          <button key={value} type="button" className="poll-scale-btn" onClick={() => onSubmit(value)}>
            <span
              className="poll-option-letter mx-auto mb-1 flex h-8 w-8 items-center justify-center rounded-lg text-sm"
              style={{ background: WORD_COLORS[i % WORD_COLORS.length], color: '#fff' }}
            >
              {value}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function SliderInput({
  slide,
  onSubmit,
}: {
  slide: PollSliderSlide;
  onSubmit: (value: number) => void;
}) {
  const step = slide.step ?? 1;
  const [value, setValue] = useState(Math.round((slide.min + slide.max) / 2 / step) * step);

  return (
    <div className="mt-8 space-y-6">
      <div className="text-center">
        <p className="font-display text-5xl font-black tabular-nums text-[var(--sala-electric-purple)]">{value}</p>
        <p className="mt-1 text-xs font-bold text-slate-600">
          {slide.min} — {slide.max}
        </p>
      </div>

      <input
        type="range"
        min={slide.min}
        max={slide.max}
        step={step}
        value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        className="sala-poll-slider w-full"
      />

      <button type="button" className="sala-btn-hero w-full text-xs" onClick={() => onSubmit(value)}>
        Confirmar valor
      </button>
    </div>
  );
}

export function formatPollAnswer(slide: PollSlide, answer: unknown): string {
  if (slide.kind === 'multiple-choice' && typeof answer === 'number') {
    return slide.options[answer] ?? String(answer);
  }
  if (slide.kind === 'word-cloud') {
    if (Array.isArray(answer)) return answer.map(String).join(', ');
    if (typeof answer === 'string') return answer;
  }
  if ((slide.kind === 'scale' || slide.kind === 'slider') && typeof answer === 'number') {
    return String(answer);
  }
  return '—';
}

export function hasPollAnswer(slide: PollSlide, answer: unknown): boolean {
  if (answer === undefined || answer === null) return false;
  if (slide.kind === 'multiple-choice') return typeof answer === 'number';
  if (slide.kind === 'word-cloud') {
    if (Array.isArray(answer)) return answer.length > 0;
    return typeof answer === 'string' && answer.trim().length > 0;
  }
  if (slide.kind === 'scale' || slide.kind === 'slider') return typeof answer === 'number';
  return false;
}
